import React, { useState, useEffect } from "react";
import { getTiers, getCFs, getCallsData } from "../utils";
import { aggregateCallsAndDistributions, calculateCapital } from "../utils";
import { xirr, convertRate } from "node-irr";
import infoIcon from "../assets/info-icon.svg";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function InvestmentMix({
  deals,
  mainData,
  updateInvestments,
  updateCfs,
  updateCapital,
}) {
  const [investments, setInvestments] = useState([
    {
      id: 1,
      name: "",
      allocation: "",
      allocationUSD: "",
      currency: "",
      netEM: "",
      exchangeRate: 1,
    },
  ]);

  const [targetExit, setTargetExit] = useState("");
  const [targetNetRoe, setTargetNetRoe] = useState(0);
  const [capital, setCapital] = useState({});
  const [xirrResult, setXirrResult] = useState(0);
  const [paybackPeriod, setPaybackPeriod] = useState(0);

  const selectedDeals = investments.map((investment) => investment.name);

  useEffect(() => {
    const tiers = investments
      .map((investment) => getTiers(mainData, investment))
      ?.filter((tier) => tier.investment.allocation);

    if (tiers.length) {
      const cfs = getCFs(tiers);
      investments.forEach((investment) => {
        investment.netEM = cfs[investment.name]?.netEM || "";
        investment.exchangeRate = deals.find(
          (deal) => deal.Deals === investment.name
        )?.["Exchange rate"];
        investment.allocationUSD =
          stringToNumber(investment.allocation) * investment.exchangeRate;
      });

      const { netEM, targetExit, aggregatedNetCashflow } =
        aggregateCallsAndDistributions(cfs);
      const netCashFlow = prepareNetCashFlowForIrr(aggregatedNetCashflow);
      if (netCashFlow.length) {
        const calculatedXirr = xirr(netCashFlow);
        const annualRate = convertRate(calculatedXirr.rate, "year");
        setXirrResult(annualRate);
        setTargetNetRoe(netEM - 1);
      }
      targetExit && setTargetExit(Math.round(targetExit));
      updateCfs(cfs);
      const validInvestments = investments.filter(
        (investment) => investment.name && investment.allocation
      );
      const totalCapital = calculateCapital(cfs, validInvestments);
      setCapital(totalCapital);
      updateCapital(totalCapital);
      const {
        capitalDistributionData: netCashFlows,
        cumulativeCFsData: cumulativeCashFlows,
      } = getCallsData(cfs);
      setPaybackPeriod(
        calculatePaybackPeriod(netCashFlows, cumulativeCashFlows)
      );
    } else {
      setXirrResult(0);
      setTargetExit("");
      setTargetNetRoe(0);
      setCapital({});
      setPaybackPeriod(0);
    }
  }, [investments, mainData, updateCfs, updateCapital, deals]);

  useEffect(() => {
    updateInvestments(investments);
  }, [investments, updateInvestments]);

  function handleInvestmentChange(id, field, value) {
    const updatedInvestments = investments.map((investment) => {
      if (investment.id === id) {
        // For numeric fields, clean the value to ensure it's numeric
        if (field === "allocation") {
          const numericValue = value.replace(/[^\d.-]/g, "");
          const allocationUSD =
            stringToNumber(numericValue) * investment.exchangeRate;
          return { ...investment, [field]: numericValue, allocationUSD };
        } else {
          investment.exchangeRate = deals.find(
            (deal) => deal.Deals === value
          )?.["Exchange rate"];
          investment.allocationUSD =
            stringToNumber(investment.allocation) * investment.exchangeRate;
          return { ...investment, [field]: value };
        }
      }
      return investment;
    });
    setInvestments(updatedInvestments);
  }
  function removeRow(id) {
    const filteredInvestments = investments.filter(
      (investment) => investment.id !== id
    );
    setInvestments(filteredInvestments);

    // Check if all rows are removed, then reset the state
    if (filteredInvestments.length === 0) {
      resetStates();
    }
  }

  function resetStates() {
    setInvestments([
      {
        id: 1,
        name: "",
        allocation: "",
        allocationUSD: "",
        currency: "",
        netEM: "",
        exchangeRate: 1,
      },
    ]);
    setCapital({});
    updateCfs([]);
    setTargetExit("");
    setTargetNetRoe(0);
    setXirrResult(0);
    setPaybackPeriod(0);
    updateInvestments([]);
    updateCapital({});
    updateCfs([]);
  }

  function addNewRow() {
    const isEmptyLastRow = investments[investments.length - 1]?.name === "";
    if (!isEmptyLastRow) {
      const newId =
        investments.length > 0
          ? Math.max(...investments.map((investment) => investment.id)) + 1
          : 1;
      const newInvestment = {
        id: newId,
        name: "",
        allocation: "",
        allocationUSD: "",
        currency: "",
        netEM: "",
        exchangeRate: 1,
      };
      setInvestments([...investments, newInvestment]);
    }
  }

  function handleUpdateAndFormat(id) {
    const updatedInvestments = investments.map((investment) => {
      if (investment.id === id) {
        const formattedAllocation = currencyFormatter.format(
          stringToNumber(investment.allocation)
        );
        return { ...investment, allocation: formattedAllocation };
      }
      return investment;
    });

    setInvestments(updatedInvestments);
  }

  function getCurrencyForDeal(dealName) {
    return deals.find((deal) => deal.Deals === dealName)?.Currency || "";
  }

  function getInvestmentStatus(dealName) {
    return deals.find((deal) => deal.Deals === dealName)?.Status || "";
  }

  function stringToNumber(input) {
    // Remove commas and any non-numeric characters except for the decimal point and minus sign
    const numericValue = input.replace(/,/g, "").replace(/[^\d.-]/g, "");
    return Number(numericValue);
  }

  function prepareNetCashFlowForIrr(data) {
    // Convert the object into an array of objects with date and amount
    const dataArray = Object.keys(data)
      .map((key) => {
        const yearSuffix = key.slice(-2); // Extract the last two digits for the year
        const year = parseInt(yearSuffix, 10) + 2000; // Convert to full year
        return {
          amount: data[key],
          date: new Date(year, 11, 31), // December 31st of the given year
        };
      })
      .sort((a, b) => a.date - b.date); // Sort by date first

    // Determine the index of the last non-zero value
    let lastNonZeroIndex = dataArray.findIndex(
      (item, index, array) =>
        item.amount !== 0 && array.slice(index + 1).every((a) => a.amount === 0)
    );

    if (lastNonZeroIndex === -1) {
      lastNonZeroIndex = dataArray.length; // If no trailing zeroes found, include all items
    } else {
      lastNonZeroIndex += 1; // Include the last non-zero item
    }

    // Filter the array to remove trailing zeroes after the last non-zero value
    return dataArray.slice(0, lastNonZeroIndex);
  }

  function calculatePaybackPeriod(netCashFlows, cumulativeCashFlows) {
    for (let i = 0; i < netCashFlows.length; i++) {
      const cumulativeDividedByNet = Math.abs(
        cumulativeCashFlows[i] / netCashFlows[i]
      );
      if (
        typeof cumulativeDividedByNet === "number" &&
        !Number.isNaN(cumulativeDividedByNet) &&
        isFinite(cumulativeDividedByNet)
      ) {
        const isCurrentCumulativeCashflowPositive = cumulativeCashFlows[i] > 0;
        const isPreviousCumulativeCashFlowNegative =
          cumulativeCashFlows[i - 1] < 0;
        if (
          isCurrentCumulativeCashflowPositive &&
          isPreviousCumulativeCashFlowNegative
        ) {
          return (i - 1 + cumulativeDividedByNet).toFixed(2);
        }
      }
    }
  }

  function getTargetNetRoe(netEm) {
    return `${((netEm - 1) * 100).toFixed(1)}%`;
  }

  return (
    <div className="bg-white shadow-md rounded-lg h-full">
      <h2
        className="bg-vennre-blue text-sm md:text-lg text-white font-semibold px-6 py-2 sm:rounded-t-lg"
        data-section-title
      >
        Investments Mix
      </h2>
      <div className="p-6 hidden invisible lg:block lg:visible">
        <div className="overflow-auto xl:max-h-52">
          <table className="leading-normal w-full">
            <thead className="sticky top-0 bg-white">
              <tr className="">
                <th className="w-1/3 px-2 py-3 border-b-2 border-vennre-gray-06 text-left text-sm font-semibold truncate">
                  Investment
                </th>
                <th className="w-[12%] px-2 py-3 border-b-2 border-vennre-gray-06 text-left text-sm font-semibold truncate">
                  Status
                </th>
                <th className="w-[12%] px-2 py-3 border-b-2 border-vennre-gray-06 text-left text-sm font-semibold truncate">
                  Scenario
                </th>
                <th className="w-[16%] px-2 py-3 border-b-2 border-vennre-gray-06 text-left text-sm font-semibold truncate">
                  <div
                    className="flex gap-1 items-center"
                    data-tooltip-id="vennre-tooltip"
                    data-tooltip-content="The minimum investment amount in Vennre deals is $25,000, with equivalents available in GBP or CHF, depending on the deal."
                    data-tooltip-place="top"
                  >
                    Allocation
                    <img
                      data-html2canvas-ignore
                      src={infoIcon}
                      alt="info-allocation-header"
                      className="mb-[1px]"
                    />
                  </div>
                </th>
                <th className="w-[10%] px-2 py-3 border-b-2 border-vennre-gray-06 text-left text-sm font-semibold truncate">
                  Currency
                </th>
                <th className="w-[20%] px-2 py-3 border-b-2 border-vennre-gray-06 text-left text-sm font-semibold truncate">
                  <div
                    className="flex gap-1 items-center"
                    data-tooltip-id="vennre-tooltip"
                    data-tooltip-content="Calculates how much profit investors make compared to how much they've invested."
                    data-tooltip-place="top"
                  >
                    Target Net ROE{" "}
                    <img
                      data-html2canvas-ignore
                      src={infoIcon}
                      alt="info-roe-header"
                      className="mb-[1px]"
                    />
                  </div>
                </th>
                <th className="w-auto px-2 py-3 border-b-2 border-vennre-gray-06 text-left text-sm font-semibold truncate"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 overflow-y-auto">
              {investments.map((investment, index) => (
                <tr key={investment.name + index} className="hover:bg-blue-100">
                  <td className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-sm">
                    <select
                      value={investment.name}
                      onChange={(e) =>
                        handleInvestmentChange(
                          investment.id,
                          "name",
                          e.target.value
                        )
                      }
                      className="custom-select appearance-none w-full text-vennre-gray-01 bg-vennre-gray-06 p-2 rounded-[4px] leading-tight focus:outline-none h-12"
                    >
                      <option value="">Select a deal</option>
                      {deals
                        .filter(
                          (deal) =>
                            !selectedDeals.includes(deal.Deals) ||
                            deal.Deals === investment.name
                        )
                        .map((deal, index) => (
                          <option key={index} value={deal.Deals}>
                            {deal.Deals}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-sm">
                    {investment.name && (
                      <span
                        className={`appearance-none text-xl bg-transparent border-none w-full font-semibold mr-2 py-1 leading-tight focus:outline-none ${
                          getInvestmentStatus(investment.name) === "Closed"
                            ? "text-red-500"
                            : "text-green-500"
                        }`}
                      >
                        •
                      </span>
                    )}
                    <span>{getInvestmentStatus(investment.name)}</span>
                  </td>
                  <td className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-sm">
                    <select
                      readOnly
                      value="Base"
                      className="w-full text-vennre-gray-01 leading-tight focus:outline-none h-8"
                    >
                      <option value="Base">Base</option>
                    </select>
                  </td>
                  <td className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-sm">
                    <input
                      type="text"
                      value={investment.allocation}
                      onChange={(e) =>
                        handleInvestmentChange(
                          investment.id,
                          "allocation",
                          e.target.value
                        )
                      }
                      onBlur={() => handleUpdateAndFormat(investment.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault(); // Prevent the default action (form submission, etc.)
                          // Manually trigger the blur or call the update logic directly
                          handleUpdateAndFormat(investment.id);
                        }
                      }}
                      placeholder="Enter amount"
                      className="appearance-none bg-transparent w-full text-vennre-gray-01 bg-vennre-gray-06 mr-3 p-2 rounded-[4px] leading-tight focus:outline-none h-12"
                    />
                  </td>
                  <td className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-sm">
                    {getCurrencyForDeal(investment.name)}
                  </td>
                  <td className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-sm">
                    <span className="appearance-none bg-transparent border-none w-full text-vennre-gray-01 mr-3 py-1 leading-tight focus:outline-none">
                      {investment.netEM
                        ? getTargetNetRoe(investment.netEM)
                        : ""}
                    </span>
                  </td>
                  <td
                    className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-base font-bold"
                    data-html2canvas-ignore
                  >
                    <button onClick={() => removeRow(investment.id)}>✕</button>
                  </td>
                </tr>
              ))}
              {deals.length > selectedDeals.length && (
                <tr className="hover:bg-blue-100" data-html2canvas-ignore>
                  <td
                    colSpan="6"
                    className="px-2 py-4 border-b border-vennre-gray-06 bg-white text-sm text-left"
                  >
                    <button
                      className="text-blue-800 font-semibold hover:text-blue-900"
                      onClick={addNewRow}
                    >
                      + Add Row
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div
        data-html2canvas-ignore
        className="block visible lg:hidden lg:invisible"
      >
        {investments.map((investment, index) => (
          <div key={investment.id} className="mb-4 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <h5 className="font-medium text-sm">Investment {index + 1}</h5>
              <button
                onClick={() => removeRow(investment.id)}
                className="ml-4 font-bold text-base"
              >
                ✕
              </button>
            </div>
            <div className="mt-6 relative">
              <select
                value={investment.name}
                onChange={(e) =>
                  handleInvestmentChange(investment.id, "name", e.target.value)
                }
                className="custom-select appearance-none w-full text-sm text-vennre-gray-01 bg-vennre-gray-06 p-2 leading-tight focus:outline-none outline outline-1 outline-slate-200 rounded-md"
              >
                <option value="">Select a deal</option>
                {deals
                  .filter(
                    (deal) =>
                      !selectedDeals.includes(deal.Deals) ||
                      deal.Deals === investment.name
                  )
                  .map((deal) => (
                    <option key={deal.Deals} value={deal.Deals}>
                      {deal.Deals}
                    </option>
                  ))}
              </select>
              <div className="absolute right-12 top-0 h-full flex items-center">
                {investment.name && (
                  <span
                    className={`appearance-none text-lg bg-transparent border-none w-full font-semibold mr-2 py-1 leading-tight focus:outline-none ${
                      getInvestmentStatus(investment.name) === "Closed"
                        ? "text-red-500"
                        : "text-green-500"
                    }`}
                  >
                    •
                  </span>
                )}
                <span className="text-xs">
                  {getInvestmentStatus(investment.name)}
                </span>
              </div>
            </div>
            <div className="mt-8">
              <h6 className="font-medium text-2xs text-vennre-gray-01 mb-1">
                Scenario
              </h6>
              <select
                readOnly
                value="Base"
                className="appearance-none w-full text-sm text-vennre-gray-01 bg-vennre-gray-06 p-2 leading-tight focus:outline-none outline outline-1 outline-slate-200 rounded-md"
              >
                <option value="Base">Base</option>
              </select>
            </div>
            <div className="mt-8">
              <div className="mb-2 flex flex-col justify-center">
                <h6 className="font-medium text-2xs text-vennre-gray-01 mb-1">
                  <div
                    className="inline-flex gap-1 items-center"
                    data-tooltip-id="vennre-tooltip"
                    data-tooltip-content="The minimum investment amount in Vennre deals is $25,000, with equivalents available in GBP or CHF, depending on the deal."
                    data-tooltip-place="top"
                  >
                    Allocation
                    <img
                      src={infoIcon}
                      alt="info-allocation-header"
                      className="mb-[1px]"
                    />
                  </div>
                </h6>
                <div className="flex items-center bg-vennre-gray-06 px-2 border-b border-slate-300 text-sm">
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter amount"
                    value={investment.allocation}
                    onChange={(e) =>
                      handleInvestmentChange(
                        investment.id,
                        "allocation",
                        e.target.value
                      )
                    }
                    onBlur={() => handleUpdateAndFormat(investment.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault(); // Prevent the default action (form submission, etc.)
                        // Manually trigger the blur or call the update logic directly
                        handleUpdateAndFormat(investment.id);
                      }
                    }}
                    className="appearance-none text-vennre-gray-01 bg-vennre-gray-06 py-2 leading-tight focus:outline-none focus:border-blue-500 grow"
                  />
                  {getCurrencyForDeal(investment.name)}
                </div>
              </div>
              <div className="mt-8 mb-2">
                <h6 className="font-medium text-2xs text-vennre-gray-01 mb-1">
                  <div
                    className="inline-flex gap-1 items-center"
                    data-tooltip-id="vennre-tooltip"
                    data-tooltip-content="Calculates how much profit investors make compared to how much they've invested."
                    data-tooltip-place="top"
                  >
                    Target Net ROE{" "}
                    <img
                      src={infoIcon}
                      alt="info-roe-header"
                      className="mb-[1px]"
                    />
                  </div>
                </h6>
                <span className="text-sm text-vennre-gray-01 py-1">
                  {investment.netEM ? getTargetNetRoe(investment.netEM) : ""}
                </span>
              </div>
            </div>
          </div>
        ))}
        {deals.length > selectedDeals.length && (
          <div className="text-left">
            <button
              className="text-blue-800 font-semibold hover:text-blue-900 m-4 text-sm"
              onClick={addNewRow}
            >
              + Add Investment
            </button>
          </div>
        )}
      </div>
      <div className="lg:p-6 p-4">
        <h3 className="font-serif text-xl block lg:hidden mb-8">
          Investment Projection
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div className="col-span-1 mb-6 lg:mb-0">
            <h6 className="inline-flex gap-1 items-center tracking-wide text-vennre-gray-01 text-2xs font-medium  truncate cursor-pointer h-7">
              Total Investments
            </h6>
            <span className="inline-block w-full font-medium text-vennre-gray-01 rounded leading-tight text-2sm">
              {capital.totalCapital
                ? `$ ${currencyFormatter.format(capital.totalCapital)}`
                : "-"}
            </span>
          </div>
          <div className="col-span-1 mb-6 lg:mb-0">
            <h6
              className="inline-flex gap-1 items-center tracking-wide text-vennre-gray-01 text-2xs font-medium  cursor-pointer h-7"
              data-tooltip-id="vennre-tooltip"
              data-tooltip-content="Projected total return expected from an investment, including capital distribution and appreciation."
              data-tooltip-place="top"
            >
              Total Distributions{" "}
              <img
                data-html2canvas-ignore
                src={infoIcon}
                alt="info-total-investments"
                className="mb-[1px]"
              />
            </h6>
            <span className="inline-block w-full font-medium text-vennre-gray-01 roundedleading-tight text-2sm">
              {targetExit ? `$ ${currencyFormatter.format(targetExit)}` : "-"}
            </span>
          </div>
          <div className="col-span-1 mb-6 lg:mb-0">
            <h6
              className="inline-flex gap-1 items-center tracking-wide text-vennre-gray-01 text-2xs font-medium  truncate cursor-pointer h-7"
              data-tooltip-id="vennre-tooltip"
              data-tooltip-content="The anticipated growth rate an investment is expected to generate over time, also known as the IRR."
              data-tooltip-place="top"
            >
              Annualised Return{" "}
              <img
                data-html2canvas-ignore
                src={infoIcon}
                alt="info-ann-return"
                className="mb-[1px]"
              />
            </h6>
            <span className="inline-block w-full font-medium text-vennre-gray-01 rounded leading-tight text-2sm">
              {(xirrResult * 100).toFixed(1)}%
            </span>
          </div>
          <div className="col-span-1 mb-6 lg:mb-0">
            <h6
              className="inline-flex gap-1 items-center tracking-wide text-vennre-gray-01 text-2xs font-medium  truncate cursor-pointer h-7"
              data-tooltip-id="vennre-tooltip"
              data-tooltip-content="Calculates how much profit investors make compared to how much they've invested."
              data-tooltip-place="top"
            >
              Target Net ROE{" "}
              <img
                data-html2canvas-ignore
                src={infoIcon}
                alt="info-roe-summary"
                className="mb-[1px]"
              />
            </h6>
            <span className="inline-block w-full font-medium text-vennre-gray-01 rounded leading-tight text-2sm">
              {(targetNetRoe * 100).toFixed(1)}%
            </span>
          </div>
          <div className="col-span-1">
            <h6
              className="inline-flex gap-1 items-center tracking-wide text-vennre-gray-01 text-2xs font-medium  truncate cursor-pointer h-7"
              data-tooltip-id="vennre-tooltip"
              data-tooltip-content="The time it takes to recover the investment or the time an investor needs to breakeven."
              data-tooltip-place="top"
            >
              Payback Period{" "}
              <img
                data-html2canvas-ignore
                src={infoIcon}
                alt="info-payback-period"
                className="mb-[1px]"
              />
            </h6>
            <span className="inline-block w-full font-medium text-vennre-gray-01 rounded leading-tight text-2sm">
              {paybackPeriod || 0} years
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
export default InvestmentMix;
